import type { CatalogUnit } from "@/lib/types";

/**
 * Espelho de EXIBIÇÃO da política comercial de venda por peso. A fonte de verdade é o backend
 * (`WeightPricingService`): o preço efetivo e o flag `smallQuantitySurcharge` vêm resolvidos nas
 * respostas de carrinho/pedido. Aqui só antecipamos a prévia e disparamos a notificação PUSH.
 */
export const SMALL_QUANTITY_THRESHOLD_KG = 1;
export const SMALL_QUANTITY_SURCHARGE_RATE = 0.1;

export const SMALL_QUANTITY_NOTICE =
  "Atenção: embalagens com peso inferior a 1 kg possuem acréscimo de 10% sobre o valor do quilograma, conforme nossa política comercial. O acréscimo é por embalagem — pedir mais unidades não remove o acréscimo.";

/** Converte a quantidade (na unidade escolhida) para kg usando o fator da unidade do catálogo. */
export function quantityToKg(quantity: number, unitName: string, catalogUnits: CatalogUnit[]): number {
  const unit = catalogUnits.find((u) => u.name === unitName);
  const factor = unit?.conversionToKg ?? 1;
  return (quantity || 0) * factor;
}

/** Peso fracionado sujeito ao acréscimo (> 0 e < 1 kg) — vale tanto para a embalagem quanto para o total. */
export function isSmallQuantity(quantityKg: number): boolean {
  return quantityKg > 0 && quantityKg < SMALL_QUANTITY_THRESHOLD_KG;
}

/** Arredondamento monetário (2 casas, HALF_UP para valores positivos), só na apresentação. */
export function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Preço base por kg a partir de uma unidade de REFERÊNCIA cadastrada: o preço registrado é sempre uma
 * referência de conversão, então `preço ÷ fator da unidade` dá o equivalente por kg. Fator inválido é
 * tratado como 1 (preço já por kg). Sem arredondamento aqui — mantém precisão interna.
 */
export function basePricePerKg(referencePrice: number, referenceConversionToKg: number): number {
  if (!referenceConversionToKg || referenceConversionToKg <= 0) return referencePrice;
  return referencePrice / referenceConversionToKg;
}

/**
 * Espelho de EXIBIÇÃO do `WeightPricingService` do backend (fonte de verdade): preço da unidade
 * selecionada = base/kg × fator da unidade, com acréscimo de 10% quando a EMBALAGEM é inferior a 1 kg
 * (`fator < 1`) OU quando o peso total (`quantidade × fator`) é inferior a 1 kg. Assim várias embalagens
 * fracionadas que somam 1 kg (2 × 500g, 5 × 200g) mantêm o acréscimo em cada unidade em vez de puxar o
 * preço cheio do kg. Arredonda a 2 casas só no resultado, como o backend.
 */
export function effectiveUnitPrice(
  basePerKg: number,
  quantity: number,
  conversionToKg: number,
): { pricePerUnit: number; surchargeApplied: boolean } {
  const factor = conversionToKg || 1;
  const quantityKg = (quantity || 0) * factor;
  const surcharge = isSmallQuantity(factor) || isSmallQuantity(quantityKg);
  const proportional = basePerKg * factor;
  const effective = surcharge ? proportional * (1 + SMALL_QUANTITY_SURCHARGE_RATE) : proportional;
  return { pricePerUnit: round2(effective), surchargeApplied: surcharge };
}
